/**
 * Page Traces AI Act — US-116
 * Audit des ExecutionTraces pour admin/cadre
 */

import { useState } from 'react';
import { apiClient } from '@/shared/services/apiClient';
import { API_ENDPOINTS } from '@/config/api';

interface Trace {
  id: string;
  job_id: string;
  ts: string | null;
  actor_user_id: string | null;
  actor_agent_id: string | null;
  event_type: string;
  outcome: string | null;
  agent_id: string;
  acte_type: string | null;
  job_status: string;
}

const EVENT_BADGE: Record<string, string> = {
  job_created: 'bg-primary',
  hitl_approved: 'bg-success',
  hitl_rejected: 'bg-danger',
  SUCCEEDED: 'bg-success',
  FAILED: 'bg-danger',
  ABORTED: 'bg-secondary',
};

const STATUS_BADGE: Record<string, string> = {
  CREATED: 'bg-light text-dark border',
  PLANNED: 'bg-info text-dark',
  RUNNING: 'bg-primary',
  WAITING_HITL: 'bg-warning text-dark',
  SUCCEEDED: 'bg-success',
  FAILED: 'bg-danger',
  ABORTED: 'bg-secondary',
};

export function TracesPage() {
  const [jobId, setJobId] = useState('');
  const [traces, setTraces] = useState<Trace[]>([]);
  const [count, setCount] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = async (filterJobId?: string) => {
    setLoading(true);
    setError(null);
    try {
      const url = filterJobId
        ? API_ENDPOINTS.adminTracesByJob(filterJobId)
        : API_ENDPOINTS.adminTraces;
      const r = await apiClient.get<{ traces: Trace[]; count: number }>(url);
      setTraces(r.traces);
      setCount(r.count);
    } catch {
      setError('Erreur de chargement des traces');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    load(jobId.trim() || undefined);
  };

  return (
    <div>
      <div className="d-flex align-items-center gap-3 mb-4">
        <h2 className="fw-bold mb-0">
          <i className="bi bi-journal-text me-2 text-primary"></i>
          Traces AI Act
        </h2>
        {count !== null && (
          <span className="badge bg-secondary">{count} trace{count > 1 ? 's' : ''}</span>
        )}
      </div>

      <p className="text-muted mb-4">
        Audit chronologique des événements d'exécution des agents (conformité AI Act).
        Zéro données patient (PHI) dans les traces.
      </p>

      {/* Recherche */}
      <form onSubmit={handleSearch} className="card mb-4">
        <div className="card-body d-flex gap-2">
          <input
            type="text"
            className="form-control"
            placeholder="ID Job (optionnel — laisser vide pour les 50 dernières)"
            value={jobId}
            onChange={(e) => setJobId(e.target.value)}
          />
          <button type="submit" className="btn btn-primary px-4" disabled={loading}>
            {loading
              ? <span className="spinner-border spinner-border-sm" />
              : <><i className="bi bi-search me-1"></i>Rechercher</>
            }
          </button>
        </div>
      </form>

      {error && (
        <div className="alert alert-danger d-flex align-items-center gap-2">
          <i className="bi bi-exclamation-circle"></i>
          {error}
        </div>
      )}

      {traces.length === 0 && count === null && !loading && (
        <div className="text-center text-muted py-5">
          <i className="bi bi-search" style={{ fontSize: '2rem' }}></i>
          <p className="mt-2">Lancez une recherche pour afficher les traces</p>
        </div>
      )}

      {traces.length === 0 && count === 0 && (
        <div className="alert alert-info">
          <i className="bi bi-info-circle me-2"></i>
          Aucune trace trouvée.
        </div>
      )}

      {traces.length > 0 && (
        <div className="card">
          <div className="card-body p-0">
            {traces.map((trace, idx) => (
              <div
                key={trace.id}
                className="px-3 py-2 d-flex align-items-start gap-3"
                style={{
                  borderBottom: idx < traces.length - 1 ? '1px solid var(--gray-100)' : 'none',
                }}
              >
                {/* Timestamp */}
                <div style={{ minWidth: '140px' }}>
                  <small className="text-muted">
                    {trace.ts ? new Date(trace.ts).toLocaleString('fr-FR') : '—'}
                  </small>
                </div>

                {/* Event */}
                <div className="flex-grow-1">
                  <div className="d-flex align-items-center gap-2 mb-1">
                    <span className={`badge ${EVENT_BADGE[trace.event_type] ?? 'bg-secondary'}`}
                      style={{ fontSize: '0.7rem' }}>
                      {trace.event_type}
                    </span>
                    {trace.outcome && (
                      <span className={`badge ${STATUS_BADGE[trace.outcome] ?? 'bg-light text-dark border'}`}
                        style={{ fontSize: '0.7rem' }}>
                        {trace.outcome}
                      </span>
                    )}
                  </div>
                  <div className="d-flex gap-3" style={{ fontSize: '0.8rem' }}>
                    <span className="text-muted">
                      Agent : <code>{trace.agent_id}</code>
                    </span>
                    {trace.acte_type && (
                      <span className="text-muted">
                        Acte : <code>{trace.acte_type}</code>
                      </span>
                    )}
                    {trace.actor_user_id && (
                      <span className="text-muted">
                        Soignant : <code>{trace.actor_user_id.slice(0, 8)}…</code>
                      </span>
                    )}
                  </div>
                </div>

                {/* Job status */}
                <div>
                  <span className={`badge ${STATUS_BADGE[trace.job_status] ?? 'bg-secondary'}`}
                    style={{ fontSize: '0.7rem' }}>
                    {trace.job_status}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
